/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment-timezone';
import { Text, DropdownEllipsisBtn } from '@blend/enterprise-components';
import { DocumentsTableColumnTypes, DocumentsColumnConfig, DocumentTableRowActions } from './types';
import { DocumentDataHeader } from './components/DocumentDataHeader';
import { DocumentNameColumn } from './components/DocumentNameColumn';
import { BlendBridge } from 'blb-types/blendBridge';
import {
  TITLE_COMMITMENT_DOC_TYPE,
  TITLE_PROPERTY_REPORT_DOC_TYPE,
  TITLE_WIRE_INSTRUCTIONS_DOC_TYPE,
  CLOSING_PACKAGE_DOC_TYPE,
  TITLE_PURCHASE_DOC_TYPES,
} from 'blb-constants/DocumentTypes';

export const DOCUMENT_TABLE_COLUMNS_DISPLAY_ORDER = [
  DocumentsTableColumnTypes.DocumentNameColumn,
  DocumentsTableColumnTypes.DocumentUploadedByColumn,
  DocumentsTableColumnTypes.DocumentUploadedOnColumn,
];

export const getFirstNameAndLastInitial = (fullName: string) => {
  const names = fullName.split(' ');
  const firstName = _.first(names);
  const lastName = _.last(names);
  return _.capitalize(firstName) + (lastName && names.length > 1 ? ` ${_.toUpper(lastName[0])}` : '');
};

export const EMPTY_IDENTIFIER = '--';
const UPLOADED_ON_DATE_FORMAT = 'MMM D [at] h:mm a';

const DOCUMENT_TYPES_UPLOADER_OVERRIDES: Record<string, string> = {
  [CLOSING_PACKAGE_DOC_TYPE]: 'System',
};

const getDocumentUploadedByDisplayName = (doc: BlendBridge.API.Document) => {
  if (isDocUploadedFromSharedInbox(doc)) {
    return 'Shared Inbox';
  }
  return getFirstNameAndLastInitial(
    DOCUMENT_TYPES_UPLOADER_OVERRIDES[doc.type] || doc.uploaderName || doc.uploadedBy || '',
  );
};

export const DOCUMENTS_COLUMN_CONFIGS = {
  [DocumentsTableColumnTypes.DocumentNameColumn]: {
    accessorFn: (loan: BlendBridge.API.Loan, document: BlendBridge.API.Document) =>
      document.fileNameOverride || document.displayFileName,
    columnWidth: 3,
    componentType: DocumentsTableColumnTypes.DocumentNameColumn,
    formatterFn: _.identity,
    headerDisplay: 'NAME',
  },
  [DocumentsTableColumnTypes.DocumentUploadedByColumn]: {
    accessorFn: (_loan: BlendBridge.API.Loan, document: BlendBridge.API.Document) =>
      getDocumentUploadedByDisplayName(document),
    columnWidth: 2,
    componentType: DocumentsTableColumnTypes.DocumentUploadedByColumn,
    formatterFn: (value: string) => value,
    headerDisplay: 'UPLOADED BY',
  },
  [DocumentsTableColumnTypes.DocumentUploadedOnColumn]: {
    accessorFn: (loan: BlendBridge.API.Loan, document: BlendBridge.API.Document) => document.primaryFile?.uploadTime,
    columnWidth: 2,
    componentType: DocumentsTableColumnTypes.DocumentUploadedOnColumn,
    formatterFn: (value?: number) => (!value ? EMPTY_IDENTIFIER : moment(value).format(UPLOADED_ON_DATE_FORMAT)),
    headerDisplay: 'UPLOADED ON',
  },
};

export const generateTableRowHeader = (
  columnConfigs: DocumentsColumnConfig[],
  handleHeaderClick: (column: DocumentsTableColumnTypes) => void,
  sortedBy: DocumentsTableColumnTypes,
  sortDir: 'asc' | 'desc',
) => {
  return [
    <div data-it="doc-header-indicator" key="indicatorHeader" />,
    ...columnConfigs.map(columnConfig => {
      const headerKey = _.kebabCase(columnConfig.headerDisplay);
      return (
        <DocumentDataHeader
          data-it={`doc-header-${headerKey}`}
          id={`doc-header-${headerKey}`}
          isSortedBy={sortedBy === columnConfig.componentType}
          key={headerKey}
          onClick={() => handleHeaderClick(columnConfig.componentType)}
          sortDir={sortDir}
        >
          {columnConfig.headerDisplay}
        </DocumentDataHeader>
      );
    }),
    <div data-it="doc-header-action" key="rowActionHeader" />,
  ];
};

export const generateTableRowDataCols = (
  loan: BlendBridge.API.Loan,
  document: BlendBridge.API.Document,
  columnConfigs: DocumentsColumnConfig[],
  rowIdx: number,
) => {
  return columnConfigs.map(columnConfig => {
    switch (columnConfig.componentType) {
      case DocumentsTableColumnTypes.DocumentNameColumn:
        return (
          <DocumentNameColumn
            data-it={`doc-row-${rowIdx}-${columnConfig.headerDisplay}`}
            document={document}
            key={`doc-row-${rowIdx}-${columnConfig.headerDisplay}`}
            loan={loan}
          />
        );
      case DocumentsTableColumnTypes.DocumentUploadedByColumn:
      case DocumentsTableColumnTypes.DocumentUploadedOnColumn:
      default:
        const { accessorFn, formatterFn } = columnConfig;
        return (
          <Text
            colorName="NIGHT_80"
            data-column={columnConfig.componentType}
            data-it={`doc-row-${rowIdx}-${columnConfig.headerDisplay}`}
            fontSize="sm"
            key={`doc-row-${rowIdx}-${columnConfig.headerDisplay}`}
          >
            {formatterFn(accessorFn(loan, document))}
          </Text>
        );
    }
  });
};

export const getTableColumnWidths = (columnConfigs: DocumentsColumnConfig[]) => [
  0, // indicator col width
  ...columnConfigs.map(config => config.columnWidth),
  1, // row action col width
];

// Table Row Actions
export const getRowActionConfigs = (
  documentActions: DocumentTableRowActions,
  document: BlendBridge.API.Document,
  sessionUserId: string,
): DropdownEllipsisBtn.Action[] => {
  return [
    {
      disabled:
        document.uploadedBy !== sessionUserId ||
        isTitleDocViewAndDownloadOnly(document) ||
        isDocUploadedFromSharedInbox(document),
      label: 'Delete',
      onClick: () => documentActions.delete(document),
    },
    {
      disabled: hasVirus(document),
      label: 'Download',
      onClick: () => documentActions.download(document),
    },
  ];
};

export const hasVirus = (document: BlendBridge.API.Document) => {
  return document.virusScanStatus === 'virus';
};

const isDocUploadedFromSharedInbox = (doc: BlendBridge.API.Document): boolean => {
  return !_.isUndefined(doc.targets) && _.includes(doc.targets, 'SHARED_INBOX');
};

const isTitleDocViewAndDownloadOnly = (document: BlendBridge.API.Document): boolean => {
  return (
    document.type === TITLE_WIRE_INSTRUCTIONS_DOC_TYPE ||
    document.type === TITLE_COMMITMENT_DOC_TYPE ||
    document.type === TITLE_PROPERTY_REPORT_DOC_TYPE ||
    _.includes(TITLE_PURCHASE_DOC_TYPES, document.type)
  );
};
